import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { influencerApi } from '@/lib/api';

/**
 * 원본: .references/gc-mono/apps/gc-zoku/redux/modules/my_campaigns.js
 * 내 캠페인 대시보드 - 참여 중인 캠페인, 이벤트, 메시지 등
 */

export interface UserCampaign {
  _id: string;
  campaignId: string;
  name?: string;
  status?: string;
  unreadMessageCount?: number;
  [key: string]: unknown;
}

export interface CampaignEvent {
  id: string;
  campaignId: string;
  type: string;
  status?: string;
  [key: string]: unknown;
}

interface CheckJoinCondition {
  status: 'checking' | 'checked' | undefined;
  error?: string;
}

interface MyCampaignsState {
  campaigns: UserCampaign[] | null;
  events: Record<string, CampaignEvent[]>;
  checkJoinConditionsByCampaignId: Record<string, CheckJoinCondition>;
  loading: boolean;
}

const initialState: MyCampaignsState = {
  campaigns: null,
  events: {},
  checkJoinConditionsByCampaignId: {},
  loading: false,
};

export const fetchMyCampaigns = createAsyncThunk(
  'myCampaigns/list',
  async () => influencerApi.post<UserCampaign[]>('my-campaigns/list'),
);

export const fetchCampaignEvents = createAsyncThunk(
  'myCampaigns/events/list',
  async (campaignId: string) => {
    const events = await influencerApi.post<CampaignEvent[]>('my-campaigns/events/list', { campaignId });
    return { campaignId, events };
  },
);

export const checkJoinConditions = createAsyncThunk(
  'myCampaigns/checkJoinConditions',
  async (campaignId: string) => {
    const result = await influencerApi.post<{ error?: string }>('my-campaigns/join-conditions/check', { campaignId });
    return { campaignId, ...result };
  },
);

export const joinCampaign = createAsyncThunk(
  'myCampaigns/join',
  async (campaignId: string) => influencerApi.post('my-campaigns/join', { campaignId }),
);

export const updateWorkorderStatus = createAsyncThunk(
  'myCampaigns/workorderStatus',
  async ({ eventId, status }: { eventId: string; status: string }) =>
    influencerApi.post<{ event: CampaignEvent; userCampaign?: UserCampaign }>('my-campaigns/work-order-status/set', { eventId, status }),
);

export const sendMessage = createAsyncThunk(
  'myCampaigns/sendMessage',
  async ({ campaignId, text }: { campaignId: string; text: string }) =>
    influencerApi.post<{ event: CampaignEvent; userCampaign?: UserCampaign }>('my-campaigns/message/send', { campaignId, text }),
);

export const sendImageMessage = createAsyncThunk(
  'myCampaigns/sendImageMessage',
  async ({ campaignId, imageUrl }: { campaignId: string; imageUrl: string }) =>
    influencerApi.post<{ event: CampaignEvent; userCampaign?: UserCampaign }>('my-campaigns/image-message/send', { campaignId, imageUrl }),
);

export const postVideo = createAsyncThunk(
  'myCampaigns/postVideo',
  async ({ campaignId, videoUrl, partnership }: { campaignId: string; videoUrl: string; partnership?: boolean }) =>
    influencerApi.post<{ event: CampaignEvent; userCampaign?: UserCampaign }>('my-campaigns/video/post', { campaignId, videoUrl, partnership }),
);

export const setVisibility = createAsyncThunk(
  'myCampaigns/setVisibility',
  async ({ groupId, campaignId, isVisible }: { groupId: string; campaignId: string; isVisible: boolean }) =>
    influencerApi.post('my-campaigns/visibility/set', { groupId, campaignId, isVisible }),
);

interface EventPayload {
  event?: CampaignEvent;
  userCampaign?: UserCampaign;
  [key: string]: unknown;
}

function updateEventInState(state: MyCampaignsState, payload: EventPayload | CampaignEvent) {
  const event: CampaignEvent = (payload as EventPayload).event ?? (payload as CampaignEvent);
  const userCampaign = (payload as EventPayload).userCampaign;

  if (!event?.campaignId) return;

  const campaignEvents = [...(state.events[event.campaignId] || [])];
  const index = campaignEvents.findIndex((e) => e.id === event.id);
  if (index !== -1) {
    campaignEvents[index] = event;
  } else {
    campaignEvents.push(event);
  }
  state.events[event.campaignId] = campaignEvents;

  if (userCampaign && state.campaigns) {
    const campIndex = state.campaigns.findIndex((c) => c.campaignId === userCampaign.campaignId);
    if (campIndex !== -1) {
      state.campaigns[campIndex] = userCampaign;
    }
  }
}

const myCampaignsSlice = createSlice({
  name: 'myCampaigns',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyCampaigns.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload;
      })
      .addCase(fetchMyCampaigns.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCampaignEvents.fulfilled, (state, action) => {
        const { campaignId, events } = action.payload;
        state.events[campaignId] = events;
        // 읽음 처리
        if (state.campaigns) {
          state.campaigns = state.campaigns.map((c) =>
            c.campaignId === campaignId ? { ...c, unreadMessageCount: 0 } : c,
          );
        }
      })
      .addCase(checkJoinConditions.pending, (state, action) => {
        const campaignId = action.meta.arg;
        state.checkJoinConditionsByCampaignId[campaignId] = { status: 'checking' };
      })
      .addCase(checkJoinConditions.fulfilled, (state, action) => {
        const { campaignId, error } = action.payload;
        state.checkJoinConditionsByCampaignId[campaignId] = { status: 'checked', error };
      })
      .addCase(checkJoinConditions.rejected, (state, action) => {
        const campaignId = action.meta.arg;
        state.checkJoinConditionsByCampaignId[campaignId] = {
          status: undefined,
          error: 'Failed to check the conditions for joining the campaign.',
        };
      })
      .addCase(updateWorkorderStatus.fulfilled, (state, action) => {
        updateEventInState(state, action.payload);
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        updateEventInState(state, action.payload);
      })
      .addCase(sendImageMessage.fulfilled, (state, action) => {
        updateEventInState(state, action.payload);
      })
      .addCase(postVideo.fulfilled, (state, action) => {
        updateEventInState(state, action.payload);
      });
  },
});

export default myCampaignsSlice.reducer;
